import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { RawMaterialService } from '../../raw-material.service';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, Subject, Subscription } from 'rxjs';
import {
  MeasurementUnit,
  RawMaterialCategoryItem,
  RawMaterialItem,
  RawMaterialRequest,
} from '../../raw-material.model';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  blobErrorMessage,
  downloadBlobResponse,
  gstPercentageValidator,
  isFileBlobResponse,
  SnackbarAlert,
  SnackbarAlertStatus,
  toNumberOrNull,
} from '../../../utils';
import { Company } from '../../../production-inventory/production-inventory.model';
import { CompanyFactoryService } from '../../../production-inventory/services/company-factory.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-raw-material',
  standalone: false,
  templateUrl: './raw-material.component.html',
  styleUrl: './raw-material.component.scss',
})
export class RawMaterialComponent implements OnInit, OnDestroy {
  @ViewChild('rawMaterialModal') rawMaterialModalRef!: TemplateRef<any>;

  @Input() units: MeasurementUnit[] = [];

  protected rawMaterialCategories: RawMaterialCategoryItem[] = [];
  protected rawMaterials: RawMaterialItem[] = [];
  protected companies: Company[] = [];
  protected isRawMaterialDataLoading: boolean = false;
  protected isSubmitting: boolean = false;
  protected isExporting: boolean = false;
  protected selectedCategoryId: string | null = null;
  protected selectedCompanyId: string | null = null;
  protected searchTerm: string = '';
  protected sortDirection: 'asc' | 'desc' = 'asc';
  protected rawMaterialForm!: FormGroup;
  protected selectedRawMaterial: RawMaterialItem | null = null;
  protected pageError: any = null;
  protected submitError: any = null;
  protected deleteError: any = null;

  private rawMaterialSubscription!: Subscription;
  private readonly searchTerms = new Subject<string>();

  /**
   * The endpoint already answers code-ascending, so `ordering` only goes on the wire
   * once the user picks a direction. The header shows the ascending arrow either way.
   */
  private isCodeSortApplied = false;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private rawMaterialService: RawMaterialService,
    private companyFactoryService: CompanyFactoryService,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getRawMaterialData();

    this.searchTerms.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => this.fetchRawMaterials());
  }

  protected get gstPercentageControl(): AbstractControl {
    return this.rawMaterialForm.get('gst_percentage')!;
  }

  /** Renders a material's default GST rate; 0 is a real value (exempt), not "unset". */
  protected gstLabel(value: unknown): string {
    const gst = toNumberOrNull(value);
    if (gst === null) return '—';

    return gst === 0 ? '0% (exempt)' : `${gst}%`;
  }

  protected get hasActiveFilters(): boolean {
    return !!(this.selectedCategoryId || this.selectedCompanyId || this.searchTerm.trim());
  }

  protected get hsnCodeControl(): AbstractControl {
    return this.rawMaterialForm.get('hsn_code')!;
  }

  protected onFilterChange(): void {
    this.fetchRawMaterials();
  }

  /**
   * Codes sort by number server-side (2, 9, 12, 100), and the list is paginated, so
   * every change refetches instead of reordering the rows already on screen.
   */
  protected onToggleCodeSort(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.isCodeSortApplied = true;
    this.fetchRawMaterials();
  }

  protected onSearchChange(term: string): void {
    this.searchTerm = term;
    this.searchTerms.next(term.trim());
  }

  /** Exports exactly what the list is currently showing — same filters, no pagination. */
  protected exportRawMaterials(): void {
    this.isExporting = true;

    this.rawMaterialService
      .export_materials(this.currentFilters)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: response => {
          // A JSON body here means the server answered with an error, not a workbook.
          if (!isFileBlobResponse(response)) {
            this.snackbarService.show(SnackbarAlert.SOMETHING_WENT_WRONG, SnackbarAlertStatus.ERROR);
            return;
          }

          downloadBlobResponse(response, 'Raw_Materials_Report.xlsx');
        },
        error: (error: HttpErrorResponse) => {
          blobErrorMessage(error, SnackbarAlert.SOMETHING_WENT_WRONG).subscribe(message =>
            this.snackbarService.show(message, SnackbarAlertStatus.ERROR)
          );
        },
      });
  }

  protected openCreateModal(): void {
    this.selectedRawMaterial = null;
    this.submitError = null;
    this.rawMaterialForm.reset();
    this.modalService.open(this.rawMaterialModalRef, { size: 'lg' });
  }

  protected openEditModal(material: RawMaterialItem): void {
    this.selectedRawMaterial = material;
    this.submitError = null;

    this.rawMaterialForm.patchValue({
      name: material.name,
      category: material.category,
      unit: material.unit.id,
      min_stock_level: material.min_stock_level,
      gst_percentage: toNumberOrNull(material.gst_percentage),
      company: material.company,
      hsn_code: material.hsn_code,
    });

    this.modalService.open(this.rawMaterialModalRef, { size: 'lg' });
  }

  deleteRawMaterial(id: string): void {
    this.deleteError = null;
    this.rawMaterialSubscription = this.rawMaterialService.delete_raw_material(id).subscribe({
      next: () => {
        this.rawMaterials = this.rawMaterials.filter(mat => mat.id !== id);
      },
      error: err => {
        this.deleteError = err;
      },
    });
  }

  protected submitRawMaterial(modalRef: any): void {
    if (this.rawMaterialForm.invalid) return;

    this.submitError = null;
    this.isSubmitting = true;

    const formValue = this.rawMaterialForm.value;

    const gstPercentage = toNumberOrNull(formValue.gst_percentage);

    const hsnCode = (formValue.hsn_code ?? '').trim();

    const reqBody: RawMaterialRequest = {
      name: formValue.name,
      category: formValue.category,
      unit: formValue.unit,
      min_stock_level: formValue.min_stock_level,
      // Sent as null rather than omitted so clearing them on an existing material sticks.
      company: formValue.company || null,
      hsn_code: hsnCode || null,
    };

    // Left blank the backend defaults to 0.00; 0 entered explicitly must still be sent.
    if (gstPercentage !== null) {
      reqBody.gst_percentage = gstPercentage;
    }

    if (this.selectedRawMaterial) {
      const updatedMaterial = { ...this.selectedRawMaterial, ...formValue };
      this.rawMaterialService
        .patch(updatedMaterial.id, reqBody)
        .pipe(finalize(() => (this.isSubmitting = false)))
        .subscribe({
          next: () => {
            this.fetchRawMaterials();
            modalRef.close();
          },
          error: err => {
            this.submitError = err;
          },
        });
    } else {
      this.rawMaterialService
        .post(reqBody)
        .pipe(finalize(() => (this.isSubmitting = false)))
        .subscribe({
          next: () => {
            this.fetchRawMaterials();
            modalRef.close();
          },
          error: err => {
            this.submitError = err;
          },
        });
    }
  }

  private fetchRawMaterials(): void {
    this.pageError = null;
    this.isRawMaterialDataLoading = true;
    this.rawMaterialService
      .list(this.listFilters)
      .pipe(finalize(() => (this.isRawMaterialDataLoading = false)))
      .subscribe({
        next: materials => (this.rawMaterials = materials || []),
        error: err => (this.pageError = err),
      });
  }

  /** What the export sends: filters and search only, since the sheet imposes its own order. */
  private get currentFilters(): { category: string | null; company: string | null; search: string } {
    return {
      category: this.selectedCategoryId,
      company: this.selectedCompanyId,
      search: this.searchTerm.trim(),
    };
  }

  /** The same filters plus the sort, which only the on-screen list takes. */
  private get listFilters(): {
    category: string | null;
    company: string | null;
    search: string;
    ordering: string | null;
  } {
    return {
      ...this.currentFilters,
      ordering: this.isCodeSortApplied ? (this.sortDirection === 'asc' ? 'code' : '-code') : null,
    };
  }

  private getRawMaterialData(): void {
    this.pageError = null;
    const rawMaterial$ = this.rawMaterialService.list(this.listFilters);
    const rawMaterialCategories$ = this.rawMaterialService.get_categories();
    const companies$ = this.companyFactoryService.listCompanies();

    this.isRawMaterialDataLoading = true;
    this.rawMaterialSubscription = forkJoin([rawMaterial$, rawMaterialCategories$, companies$])
      .pipe(finalize(() => (this.isRawMaterialDataLoading = false)))
      .subscribe({
        next: ([rawMaterialsResponse, rawMaterialCategories, companies]: [
          RawMaterialItem[],
          RawMaterialCategoryItem[],
          Company[],
        ]) => {
          this.rawMaterials = rawMaterialsResponse || [];
          this.rawMaterialCategories = rawMaterialCategories;
          this.companies = companies || [];
        },
        error: err => (this.pageError = err),
      });
  }

  private initForm(): void {
    this.rawMaterialForm = this.fb.group({
      name: ['', Validators.required],
      category: ['', Validators.required],
      unit: ['', Validators.required],
      min_stock_level: [null, Validators.required],
      // All three are backfill fields — optional on create, editable later via PATCH.
      gst_percentage: [null, gstPercentageValidator],
      company: [null],
      hsn_code: [null, Validators.maxLength(15)],
    });
  }

  ngOnDestroy(): void {
    this.rawMaterialSubscription?.unsubscribe();
    this.searchTerms.complete();
  }
}
